import { json, type LoaderFunctionArgs } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import {
    getCombinedNotifications,
    getTopNavigation,
} from '@modules/navigation';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getShoppingCartSummary } from '@modules/ecommerce/api/get-shopping-cart-summary.server';
import { hasFeature } from '@modules/root';
import { getPermissions } from '~/modules/user';
import { PermissionEnum, SortDirection } from 'generated/graphql';
import { getSession } from '~/modules/root/util/session.server';
import { getProfessionalBookingLink } from '@modules/professional/api/get-professional-booking-link.server';
import AppPage from '@modules/root/page/AppPage';
import { toggleFeatures } from '~/modules/root/util/toggle-features';
import { getDesignBoardItems } from '~/modules-rocco/design-board/api/get-design-board-items.server';
import { getBottomContent } from '@modules/root/api/get-bottom-content.server';
import { getFilterDrawerCookieValue } from '~/modules/search/hook/use-filter-drawer-preference';
/**
 * global catch and error boundary for all pages under __public
 * Child routes can still catch their own but if they don't we'll end up here
 * root.tsx will still render top nav etc
 */
export { ErrorBoundary, links } from '@modules/root/page/AppPage';

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { appDisplayMode, device, user, featureFlags } = context;
    const response = toggleFeatures(request);
    if (response) throw response;

    const url = new URL(request.url);
    if (
        url.pathname.startsWith('/search') &&
        appDisplayMode !== 'desktop' &&
        !device.isBot
    ) {
        const [notifications, abTestConfig, session, designBoardItems] =
            await Promise.all([
                getCombinedNotifications(context),
                context.getABTests(),
                getSession(request),
                getDesignBoardItems(
                    {
                        offset: 0,
                        limit: 2000,
                        sortBy: { LastEdited: SortDirection.Desc },
                    },
                    context
                ),
            ]);

        const bookConsultationEnabled = hasFeature(
            featureFlags,
            'bookconsultation'
        );

        const ecommerceActiveEnabled = hasFeature(
            featureFlags,
            'ecommerce_active'
        );
        const ba2NavEnabled = hasFeature(featureFlags, 'ba_two_new_nav');

        const autoSubmitSuccess = session.getAutoSubmitSuccess();

        const availableForWorkEnabled = hasFeature(
            featureFlags,
            'available_for_work'
        );
        return json(
            {
                user,
                featureFlags,
                topNavigation: null,
                notifications,
                localisation: null,
                shoppingCartSummary: null,
                ecommerceActiveEnabled,
                ba2NavEnabled,
                bookConsultationEnabled,
                canApproveContent: getPermissions(
                    user,
                    PermissionEnum.BmContentApprove
                ),
                canCreateProfessional: getPermissions(
                    user,
                    PermissionEnum.BmProfessionalCreate
                ),

                toastMessage: session.getToastMessage(),
                autoSubmitSuccess,
                abTestConfig,
                showReseneSponsor: false,
                availableForWorkEnabled,
                professionalBookingLink: null,
                designBoardItems,
                bottomContent: null,
                filterDrawerCookieValue: getFilterDrawerCookieValue(request),
            },
            {
                headers: await session.getHeaders(),
            }
        );
    }

    const [
        topNavigation,
        localisation,
        notifications,
        shoppingCartSummary,
        abTestConfig,
        session,
        professionalBookingLink,
        designBoardItems,
        bottomContent,
    ] = await Promise.all([
        getTopNavigation(context),
        getPageLocalisation(args),
        getCombinedNotifications(context),
        getShoppingCartSummary(context),
        context.getABTests(),
        getSession(request),
        getProfessionalBookingLink(context, request),
        getDesignBoardItems(
            {
                offset: 0,
                limit: 2000,
                sortBy: { LastEdited: SortDirection.Desc },
            },
            context
        ),
        getBottomContent(context),
    ]);

    // Currently not being used.
    // const tabletEnabled = hasFeature(user.FeatureFlags, 'enable_tablet');

    const bookConsultationEnabled = hasFeature(
        featureFlags,
        'bookconsultation'
    );

    const ecommerceActiveEnabled = hasFeature(featureFlags, 'ecommerce_active');
    const ba2NavEnabled = hasFeature(featureFlags, 'ba_two_new_nav');

    const autoSubmitSuccess = session.getAutoSubmitSuccess();

    const availableForWorkEnabled = hasFeature(
        featureFlags,
        'available_for_work'
    );

    return json(
        {
            user,
            featureFlags,
            topNavigation,
            localisation,
            notifications,
            shoppingCartSummary,
            ecommerceActiveEnabled,
            ba2NavEnabled,
            bookConsultationEnabled,
            canApproveContent: getPermissions(
                user,
                PermissionEnum.BmContentApprove
            ),
            canCreateProfessional: getPermissions(
                user,
                PermissionEnum.BmProfessionalCreate
            ),

            toastMessage: session.getToastMessage(),
            autoSubmitSuccess,
            abTestConfig,
            showReseneSponsor: false,
            availableForWorkEnabled,
            professionalBookingLink: bookConsultationEnabled
                ? professionalBookingLink
                : null,
            designBoardItems,
            bottomContent,
            filterDrawerCookieValue: getFilterDrawerCookieValue(request),
        },
        {
            headers: await session.getHeaders({
                // Make sure user data is not cached.
                'Cache-Control': 'no-cache',
            }),
        }
    );
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formMethod,
    nextUrl,
}) => {
    if (
        // Temporary fix for login redirection issue.
        (!formMethod && nextUrl.pathname.startsWith('/search')) ||
        formMethod === 'GET'
    ) {
        // Do not revalidate on page navigation or URL search param change.
        return false;
    }
    return defaultShouldRevalidate;
};

const App = () => <AppPage />;

export default App;
